/**
 * What is stopping „Przypisz plan", said once, and where the trainer has to go to fix it.
 *
 * ── One resolver, because a list of refusals is the wrong shape for this footer ──────────────
 * `assignmentRequest` answers with EVERY refusal, which is right: it is the mapper, and a caller
 * that wants them all must be able to have them. The mockup's footer has room for one sentence
 * beside the primary, and four sentences stacked there would push the button off a 620px window.
 * So this picks the FIRST one in the order the form is filled in — which is also the order the
 * trainer will fix them in, so the sentence changes as they work rather than naming the last
 * problem first.
 *
 * ── It names a FIELD, because a refused primary moves focus to it ───────────────────────────
 * View ADR §12, and the create dialog's own lesson: the reason used to live in a `clip-path`ed
 * span, so the only trainer ever told what was wrong was one using a screen reader. Three things
 * say it here too — the footer sentence, the button's `title`, and the press itself, which puts the
 * caret in the field that unblocks it. That last one is the difference between being told what is
 * wrong and being taken to it, and it needs the field as data.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. The key and the field leave as
 * tokens.
 */

import type { AssignField, AssignSurface } from './assignForm';
import type { AssignRefusal } from './assignRequest';

/**
 * What the footer says and what the primary does when it is pressed anyway.
 *
 * The refusal travels as DATA, not as a key and a value bag: `assignRefusalCopy` already words
 * every arm of `AssignRefusal` and is walked by a test that resolves each one against both
 * bundles, so a second table of keys and interpolations here would be the same sentences written
 * twice — and only one of the two copies would be covered.
 */
export type AssignBlocking =
  /** The plan read has not landed. Not a refusal the mapper can produce — see below. */
  | { readonly kind: 'pending'; readonly field: null }
  | {
      readonly kind: 'refused';
      readonly refusal: AssignRefusal;
      /** Which field takes focus on a refused press, or `null` when there is nowhere useful to go. */
      readonly field: AssignField | null;
    };

/**
 * The order the trainer answers in, which is the order they are told about.
 *
 * `noClient` before `noStartDate` before the times: that is the form top to bottom, and it is also
 * the backend's own dependency order — there is no point naming a missing hour to somebody who has
 * not said who is training.
 */
const ORDER: readonly AssignRefusal['kind'][] = [
  'noClient',
  'noStartDate',
  'startTooEarly',
  'startTooLate',
  'noStationaryTimes',
  'malformedTime',
  'timeOffPattern',
  'overrideOutOfRange',
];

/** Which field each refusal sends the trainer to. */
const FIELD: Readonly<Record<AssignRefusal['kind'], AssignField | null>> = {
  noClient: 'client',
  noStartDate: 'start',
  startTooEarly: 'start',
  startTooLate: 'start',
  noStationaryTimes: 'times',
  malformedTime: 'times',
  timeOffPattern: 'times',
  // The projection, not a form field. The trainer has to move a chip back inside the window, and
  // there is no input to put a caret in.
  overrideOutOfRange: null,
};

/** The one thing to say, or `null` when the assignment is ready to send. */
export function assignBlocking(
  refusals: readonly AssignRefusal[],
  surface: AssignSurface,
): AssignBlocking | null {
  // A plan that has not loaded blocks the primary too, and it is not a refusal the mapper can
  // produce: there is no target to judge a selection against. Reported first, because every other
  // sentence would be about a form the trainer cannot see yet.
  if (surface === 'pending') return { kind: 'pending', field: null };

  const first = ORDER.map((kind) => refusals.find((refusal) => refusal.kind === kind)).find(
    (refusal) => refusal !== undefined,
  );
  if (first === undefined) return null;

  return { kind: 'refused', refusal: first, field: FIELD[first.kind] };
}
